<template>
    <main
        class="min-w-0 flex-1 border-t border-taupe-200 lg:flex overflow-y-auto overflow-x-hidden"
    >
        <!-- Primary column -->
        <section
            aria-labelledby="primary-heading"
            class="flex h-full min-w-0 flex-1 flex-col lg:order-last pt-2 sm:pt-4 lg:pt-6 lg:max-w-7xl lg:mx-auto"
        >
            <h1 id="primary-heading" class="sr-only">Project Segments</h1>
            <div class="px-4 sm:px-6 lg:px-8">
                <nav class="flex items-center gap-2 text-sm">
                    <router-link
                        :to="{
                            name: 'ProjectSegmentsScreen',
                            params: environmentRouteParams,
                        }"
                        class="text-taupe-500 hover:text-sky-500"
                    >
                        Segments
                    </router-link>
                </nav>
                <div v-if="!loading && result" class="mt-1">
                    <h1 class="text-2xl font-medium text-taupe-800">
                        {{ result.name }}
                    </h1>
                </div>

                <zen-loading-indicator v-if="loading" />

                <div v-if="!loading && result" class="lg:flex lg:space-x-8">
                    <div class="mt-8 w-full lg:w-2/3">
                        <div>
                            <div v-if="result.contexts != null">
                                <zen-grid
                                    :flush="true"
                                    :empty="resultContexts.length === 0"
                                >
                                    <template v-slot:title>Contexts</template>
                                    <zen-grid-table>
                                        <zen-grid-header>
                                            <zen-grid-header-row>
                                                <zen-grid-header-cell
                                                    :hidable="false"
                                                    title="Name"
                                                />
                                                <zen-grid-header-cell
                                                    alignment="center"
                                                    title="Type"
                                                />
                                                <zen-grid-header-cell
                                                    title="Identifier"
                                                />
                                                <zen-grid-header-cell
                                                    alignment="center"
                                                    title="Actions"
                                                />
                                            </zen-grid-header-row>
                                        </zen-grid-header>
                                        <zen-grid-body
                                            :results="filteredResultContexts"
                                            :loading="loading"
                                        >
                                            <zen-grid-body-row
                                                v-for="context in filteredResultContexts"
                                                :key="contextRowKey(context)"
                                            >
                                                <zen-grid-body-cell
                                                    :hidable="false"
                                                    :primary="true"
                                                >
                                                    <div
                                                        class="flex items-center gap-1.5"
                                                    >
                                                        <template
                                                            v-if="
                                                                isPersistedContext(
                                                                    context
                                                                )
                                                            "
                                                        >
                                                            <zen-grid-body-cell-primary-link
                                                                :to="{
                                                                    name: 'ProjectContextDetailScreen',
                                                                    params: {
                                                                        projectKey:
                                                                            currentEnvironment.projectKey,
                                                                        environmentKey:
                                                                            currentEnvironment.environmentKey,
                                                                        contextUlid:
                                                                            context.ulid,
                                                                    },
                                                                }"
                                                            >
                                                                {{
                                                                    context.name
                                                                }}
                                                            </zen-grid-body-cell-primary-link>
                                                        </template>
                                                        <template v-else>
                                                            <span
                                                                class="font-medium text-taupe-800"
                                                                >{{
                                                                    context.name
                                                                }}</span
                                                            >
                                                        </template>

                                                        <zen-tooltip
                                                            v-if="
                                                                !isPersistedContext(
                                                                    context
                                                                )
                                                            "
                                                            content="This context does not exist yet. It will be created automatically when first seen."
                                                            v-slot="{
                                                                setReference,
                                                                ariaAttrs,
                                                                show,
                                                                hide,
                                                            }"
                                                        >
                                                            <span
                                                                :ref="
                                                                    setReference
                                                                "
                                                                v-bind="
                                                                    ariaAttrs
                                                                "
                                                                tabindex="0"
                                                                class="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-sky-300 bg-sky-50 text-[10px] font-semibold leading-none text-sky-700"
                                                                aria-label="Info"
                                                                @mouseenter="
                                                                    show
                                                                "
                                                                @mouseleave="
                                                                    hide
                                                                "
                                                                @focus="show"
                                                                @blur="hide"
                                                            >
                                                                i
                                                            </span>
                                                        </zen-tooltip>
                                                    </div>

                                                    <zen-grid-mobile-detail>
                                                        <zen-grid-mobile-detail-item
                                                            label="Type"
                                                        >
                                                            <zen-inline-pill
                                                                v-if="
                                                                    isPersistedContext(
                                                                        context
                                                                    )
                                                                "
                                                                :value="
                                                                    context.type
                                                                "
                                                            />
                                                            <zen-inline-pill
                                                                v-else
                                                                value="Any"
                                                                color-class="taupe-500"
                                                            />
                                                        </zen-grid-mobile-detail-item>
                                                        <zen-grid-mobile-detail-item
                                                            label="Identifier"
                                                        >
                                                            <span
                                                                class="font-mono"
                                                                >{{
                                                                    context.identifier
                                                                }}</span
                                                            >
                                                        </zen-grid-mobile-detail-item>
                                                        <zen-grid-mobile-detail-item
                                                            label="Actions"
                                                        >
                                                            <zen-button
                                                                v-if="
                                                                    can(
                                                                        PERMISSIONS.SEGMENT_UPDATE,
                                                                        permissionScope
                                                                    )
                                                                "
                                                                variant="link-danger"
                                                                @click="
                                                                    openRemoveContextModal(
                                                                        context
                                                                    )
                                                                "
                                                            >
                                                                Remove
                                                            </zen-button>
                                                        </zen-grid-mobile-detail-item>
                                                    </zen-grid-mobile-detail>
                                                </zen-grid-body-cell>
                                                <zen-grid-body-cell
                                                    alignment="center"
                                                >
                                                    <zen-inline-pill
                                                        v-if="
                                                            isPersistedContext(
                                                                context
                                                            )
                                                        "
                                                        :value="context.type"
                                                    />
                                                    <zen-inline-pill
                                                        v-else
                                                        value="Any"
                                                        color-class="taupe-500"
                                                    />
                                                </zen-grid-body-cell>
                                                <zen-grid-body-cell>
                                                    <span class="font-mono">
                                                        {{ context.identifier }}
                                                    </span>
                                                </zen-grid-body-cell>
                                                <zen-grid-body-cell
                                                    alignment="center"
                                                >
                                                    <zen-table-icon
                                                        v-if="
                                                            can(
                                                                PERMISSIONS.SEGMENT_UPDATE,
                                                                permissionScope
                                                            )
                                                        "
                                                        @click="
                                                            openRemoveContextModal(
                                                                context
                                                            )
                                                        "
                                                        :srText="
                                                            'Remove ' +
                                                            context.name +
                                                            ' Context'
                                                        "
                                                        tooltipText="Remove"
                                                    >
                                                        <zen-icon-remove />
                                                    </zen-table-icon>
                                                </zen-grid-body-cell>
                                            </zen-grid-body-row>
                                        </zen-grid-body>
                                    </zen-grid-table>

                                    <template v-slot:filters>
                                        <input
                                            type="search"
                                            id="segment-context-search"
                                            name="segment-context-search"
                                            v-model="filters.contextSearch"
                                            autocomplete="off"
                                            class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-taupe-300 focus:ring-taupe-500 sm:text-sm lg:text-base"
                                            placeholder="Filter contexts..."
                                            aria-label="Filter contexts"
                                        />
                                    </template>
                                    <template v-slot:actions>
                                        <zen-button
                                            v-if="
                                                can(
                                                    PERMISSIONS.SEGMENT_UPDATE,
                                                    permissionScope
                                                )
                                            "
                                            variant="primary"
                                            size="small"
                                            @click="addContext()"
                                        >
                                            Add a context
                                        </zen-button>
                                    </template>
                                </zen-grid>
                            </div>
                            <zen-empty-table-message
                                v-if="resultContexts.length === 0"
                                title="No contexts yet"
                                icon="ZenIconUser"
                            >
                                There are no contexts associated with this
                                segment.
                            </zen-empty-table-message>
                            <zen-empty-table-message
                                v-else-if="filteredResultContexts.length === 0"
                                title="No matching contexts"
                                icon="ZenIconUser"
                            >
                                No contexts match your filter.
                            </zen-empty-table-message>
                        </div>
                    </div>

                    <div class="my-8 w-full lg:w-1/3">
                        <div>
                            <div
                                class="mb-6 rounded-xl border border-taupe-200 bg-white shadow-xs"
                            >
                                <dl class="divide-y divide-taupe-100">
                                    <div
                                        class="rounded-t-xl bg-taupe-50 px-4 py-4 sm:px-5"
                                    >
                                        <h3
                                            class="text-base font-medium text-taupe-800"
                                        >
                                            Detail
                                        </h3>
                                    </div>
                                    <div
                                        class="grid grid-cols-[110px_minmax(0,1fr)] items-center gap-x-4 px-4 py-4 sm:px-5"
                                    >
                                        <dt
                                            class="text-xs font-medium uppercase tracking-wide text-taupe-500"
                                        >
                                            Count
                                        </dt>
                                        <dd
                                            class="text-sm leading-6 text-taupe-900"
                                        >
                                            <span
                                                class="inline-flex items-center rounded-md bg-taupe-100 px-2 py-0.5 text-sm font-medium text-taupe-800"
                                            >
                                                {{ result.count }}
                                            </span>
                                        </dd>
                                    </div>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <add-segment-contexts-slide-in
            :is-open="modals.addContext.show"
            v-model:search="modals.addContext.data.search"
            :error="modals.addContext.error"
            :adding="modals.addContext.adding"
            :adding-key="modals.addContext.addingKey"
            :results="contextSearchResults"
            :context-row-key="contextRowKey"
            :is-persisted-context="isPersistedContext"
            @close="toggleAddContextModal"
            @add="confirmAddContext"
        />

        <remove-segment-context-modal
            :is-open="modals.removeContext.show"
            @close="toggleRemoveContextModal"
            @confirm="confirmRemoveContext"
        />
    </main>
</template>

<script>
import ContextService from '@/services/context.service'
import SegmentService from '@/services/segment.service'
import AddSegmentContextsSlideIn from '@/screens/trusted/project/modals/AddSegmentContextsSlideIn.vue'
import RemoveSegmentContextModal from '@/screens/trusted/project/modals/RemoveSegmentContextModal.vue'

import { errorMessage } from '@/utils/notifications'
import { removeBy, upsertBy } from '@/utils/list-mutations'
import { usePermissions, PERMISSIONS } from '@/composables/usePermissions'
import { useCurrentEnvironment } from '@/composables/useCurrentEnvironment'

export default {
    name: 'ProjectSegmentDetailScreen',
    components: {
        AddSegmentContextsSlideIn,
        RemoveSegmentContextModal,
    },
    setup() {
        const { can } = usePermissions()
        const { current: currentEnvironment } = useCurrentEnvironment()
        return { can, PERMISSIONS, currentEnvironment }
    },
    computed: {
        contextSearchResults() {
            const search = this.modals.addContext.data.search.trim()
            if (search === '') {
                return []
            }

            const normalizedSearch = search.toLowerCase()
            let matches = 0
            const results = this.modals.addContext.data.results
                .filter((context) => context !== null)
                .filter((context) => {
                    const identifier = (context.identifier || '').toLowerCase()
                    const name = (context.name || '').toLowerCase()

                    if (
                        (name.includes(normalizedSearch) ||
                            identifier.includes(normalizedSearch)) &&
                        !this.currentContextIdentifiers.includes(identifier) &&
                        !this.currentContextUlids.includes(context.ulid) &&
                        matches < 10
                    ) {
                        matches++
                        return context
                    }

                    return false
                })

            if (
                !this.currentContextIdentifiers.includes(normalizedSearch) &&
                !results.some(
                    (context) =>
                        (context.identifier || '').toLowerCase() ===
                        normalizedSearch
                )
            ) {
                results.push({
                    ulid: null,
                    type: null,
                    name: search,
                    identifier: search,
                })
            }

            return results
                .sort((left, right) => {
                    const leftPersisted = this.isPersistedContext(left)
                    const rightPersisted = this.isPersistedContext(right)

                    if (leftPersisted !== rightPersisted) {
                        return leftPersisted ? -1 : 1
                    }

                    return (left.name || '').localeCompare(right.name || '')
                })
                .slice(0, 10)
        },
        resultContexts() {
            if (this.result == null || this.result.contexts == null) {
                return []
            }

            return this.result.contexts.data
                .filter((context) => context !== null)
                .sort((left, right) => {
                    const leftPersisted = this.isPersistedContext(left)
                    const rightPersisted = this.isPersistedContext(right)

                    if (leftPersisted !== rightPersisted) {
                        return leftPersisted ? -1 : 1
                    }

                    return (left.name || '').localeCompare(right.name || '')
                })
        },
        filteredResultContexts() {
            const search = this.filters.contextSearch.trim().toLowerCase()

            const filtered =
                search === ''
                    ? this.resultContexts
                    : this.resultContexts.filter((context) => {
                          const name = (context.name || '').toLowerCase()
                          const identifier = (
                              context.identifier || ''
                          ).toLowerCase()
                          const type = this.isPersistedContext(context)
                              ? (context.type || '').toLowerCase()
                              : 'any'

                          return (
                              name.includes(search) ||
                              identifier.includes(search) ||
                              type.includes(search)
                          )
                      })

            return [...filtered].sort((left, right) => {
                const leftPersisted = this.isPersistedContext(left)
                const rightPersisted = this.isPersistedContext(right)

                if (leftPersisted !== rightPersisted) {
                    return leftPersisted ? -1 : 1
                }

                return (left.name || '').localeCompare(right.name || '')
            })
        },
        currentContextUlids() {
            if (this.result == null) {
                return []
            }

            return this.resultContexts
                .map((c) => c.ulid)
                .filter((ulid) => ulid !== null)
        },
        currentContextIdentifiers() {
            if (this.result == null) {
                return []
            }

            return this.resultContexts
                .map((c) => (c.identifier || '').toLowerCase())
                .filter((identifier) => identifier !== '')
        },
        environmentRouteParams() {
            return {
                projectKey: this.currentEnvironment.projectKey,
                environmentKey: this.currentEnvironment.environmentKey,
            }
        },
        permissionScope() {
            return {
                projectUlid: this.currentEnvironment.projectUlid,
                environmentUlid: this.currentEnvironment.environmentUlid,
            }
        },
        hasEnvironmentRouteParams() {
            return (
                this.environmentRouteParams.projectKey !== null &&
                this.environmentRouteParams.environmentKey !== null
            )
        },
    },
    data() {
        return {
            loading: true,
            result: null,
            filters: {
                contextSearch: '',
            },
            modals: {
                addContext: {
                    adding: false,
                    addingKey: null,
                    error: null,
                    loading: false,
                    show: false,
                    data: {
                        search: '',
                        results: [],
                    },
                },
                removeContext: {
                    show: false,
                    context: null,
                },
            },
        }
    },
    methods: {
        contextRowKey(context) {
            return context.ulid || `identifier-${context.identifier}`
        },
        isPersistedContext(context) {
            return context.ulid !== null && context.ulid !== undefined
        },
        addContext() {
            this.toggleAddContextModal()
            this.modals.addContext.loading = true
            ContextService.lookup(
                this.currentEnvironment.projectKey,
                this.currentEnvironment.environmentKey
            ).then(
                (data = []) => {
                    this.modals.addContext.data.results = Array.isArray(data)
                        ? data
                        : (data?.data ?? [])
                    this.modals.addContext.loading = false
                    this.modals.addContext.error = null
                },
                (error) => {
                    this.modals.addContext.error = error
                    this.modals.addContext.loading = false
                }
            )
        },
        confirmAddContext(context) {
            if (this.modals.addContext.adding) {
                return
            }

            this.modals.addContext.error = null

            const identifier = (context.identifier || '').trim()

            if (!this.isPersistedContext(context) && identifier === '') {
                this.modals.addContext.error =
                    'A context identifier is required.'
                return
            }

            this.modals.addContext.adding = true
            this.modals.addContext.addingKey = this.contextRowKey(context)

            const request = this.isPersistedContext(context)
                ? SegmentService.attachContext(
                      this.currentEnvironment.projectKey,
                      this.currentEnvironment.environmentKey,
                      this.$route.params.segmentUlid,
                      context.ulid
                  )
                : SegmentService.attachContextByIdentifier(
                      this.currentEnvironment.projectKey,
                      this.currentEnvironment.environmentKey,
                      this.$route.params.segmentUlid,
                      identifier
                  )

            request
                .then(
                    (response) => {
                        this.upsertContextInResult(response.data)
                    },
                    (error) => {
                        this.modals.addContext.error = error
                    }
                )
                .finally(() => {
                    this.modals.addContext.adding = false
                    this.modals.addContext.addingKey = null
                })
        },
        confirmRemoveContext() {
            const context = this.modals.removeContext.context

            if (context === null) {
                return
            }

            const request = this.isPersistedContext(context)
                ? SegmentService.detachContext(
                      this.currentEnvironment.projectKey,
                      this.currentEnvironment.environmentKey,
                      this.$route.params.segmentUlid,
                      context.ulid
                  )
                : SegmentService.detachContextByIdentifier(
                      this.currentEnvironment.projectKey,
                      this.currentEnvironment.environmentKey,
                      this.$route.params.segmentUlid,
                      context.identifier
                  )

            request.then(
                () => {
                    this.removeContextFromResult(context)
                    this.toggleRemoveContextModal()
                },
                (error) => {
                    errorMessage(error)
                }
            )
        },
        openRemoveContextModal(context) {
            this.modals.removeContext.context = context
            this.toggleRemoveContextModal()
        },
        refresh() {
            if (!this.hasEnvironmentRouteParams) {
                this.loading = false
                return Promise.resolve()
            }

            this.loading = true
            return SegmentService.get(
                this.currentEnvironment.projectKey,
                this.currentEnvironment.environmentKey,
                this.$route.params.segmentUlid
            ).then(
                (data) => {
                    this.result = data.data
                    this.loading = false
                },
                async () => {
                    await this.$router.push({
                        name: 'ProjectSegmentsScreen',
                        params: this.environmentRouteParams,
                    })
                }
            )
        },
        // Add the attached context to the list. An attach that is already
        // listed leaves the list as it is. A resolved context replaces the
        // unresolved row that had the same identifier.
        upsertContextInResult(context) {
            const before = this.result.contexts.data.filter(
                (item) => item !== null
            )
            const identifier = (context.identifier || '').toLowerCase()
            const withoutPlaceholder = this.isPersistedContext(context)
                ? before.filter(
                      (item) =>
                          this.isPersistedContext(item) ||
                          (item.identifier || '').toLowerCase() !== identifier
                  )
                : before
            const after = upsertBy(withoutPlaceholder, context, (item) =>
                this.contextRowKey(item)
            )

            this.result.contexts.data = after
            if (after.length > before.length) {
                this.result.count = (this.result.count ?? 0) + 1
            }
        },
        // Remove the detached context from the list. Unresolved rows match
        // by identifier, because they have no ulid.
        removeContextFromResult(context) {
            const before = this.result.contexts.data.filter(
                (item) => item !== null
            )
            const after = removeBy(
                before,
                this.contextRowKey(context),
                (item) => this.contextRowKey(item)
            )

            this.result.contexts.data = after
            if (after.length < before.length) {
                this.result.count = Math.max(0, (this.result.count ?? 0) - 1)
            }
        },
        toggleAddContextModal() {
            this.modals.addContext.show = !this.modals.addContext.show
            if (!this.modals.addContext.show) {
                this.modals.addContext.data.search = ''
                this.modals.addContext.error = null
            }
        },
        toggleRemoveContextModal() {
            this.modals.removeContext.show = !this.modals.removeContext.show
            if (!this.modals.removeContext.show) {
                this.modals.removeContext.context = null
            }
        },
    },
    mounted() {
        this.refresh()
    },
}
</script>
